import constantURL from "./ConstantURL";
import { getStoredUserToken } from "../utils/sessionContext";
import { isPasswordValid, PASSWORD_CRITERIA_INVALID_MESSAGE } from "../utils/passwordCriteria";
import { PROFILE_ROLE_CAREGIVER, PROFILE_ROLE_FAMILY_MEMBER } from "./profileApi";

export const INCORRECT_CURRENT_PASSWORD_MESSAGE = "Current password is incorrect.";
export const RESET_PASSWORD_SUCCESS_MESSAGE = "Password has been reset successfully.";
export const INCORRECT_CURRENT_LOGIN_CODE_MESSAGE = "Current login code is incorrect.";
export const RESET_LOGIN_CODE_SUCCESS_MESSAGE = "Login code has been reset successfully.";
export const LOGIN_CODE_INVALID_MESSAGE = "Login code must be 4 digits.";
export const LOGIN_CODES_MISMATCH_MESSAGE = "Login codes do not match.";

const INVALID_CURRENT_PASSWORD_CODE = "INVALID_CURRENT_PASSWORD";

const sanitizeLoginCode = (value) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, 4);

const isValidLoginCode = (value) => sanitizeLoginCode(value).length === 4;

const normalizeRole = (role, fallback) =>
  String(role || fallback)
    .trim()
    .toUpperCase();

const isIncorrectCurrentPasswordResponse = (response) => {
  const message = String(response?.message || "").toLowerCase();
  const code = String(response?.data?.code || "").toUpperCase();

  return (
    code === INVALID_CURRENT_PASSWORD_CODE ||
    code === "INCORRECT_PASSWORD" ||
    code === "WRONG_PASSWORD" ||
    code === "CURRENT_PASSWORD_INVALID" ||
    message.includes("current password is incorrect") ||
    message.includes("current password incorrect") ||
    message.includes("incorrect password") ||
    message.includes("invalid password") ||
    message.includes("wrong password")
  );
};

const callResetPasswordApi = async ({ email, role, currentPassword, newPassword }) => {
  try {
    const response = await fetch(constantURL.authResetPassword, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getStoredUserToken() || ""}`,
      },
      body: JSON.stringify({
        email,
        role,
        currentPassword,
        newPassword,
      }),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    return data;
  } catch {
    return null;
  }
};

const mapResetPasswordResponse = (response, { currentField, incorrectMessage, successMessage, failureMessage }) => {
  if (response?.status === "success") {
    return {
      success: true,
      message: response.message || successMessage,
    };
  }

  if (isIncorrectCurrentPasswordResponse(response)) {
    return {
      success: false,
      field: currentField,
      message: response?.message || incorrectMessage,
    };
  }

  return {
    success: false,
    message: response?.message || failureMessage,
  };
};

export const resetAuthenticatedPassword = async ({ emailId, currentPassword, password, confirmPassword, role = PROFILE_ROLE_FAMILY_MEMBER }) => {
  const email = String(emailId || "")
    .trim()
    .toLowerCase();
  const current = String(currentPassword ?? "");
  const newPassword = String(password ?? "");
  const confirm = String(confirmPassword ?? "");

  if (!email) {
    return { success: false, message: "Unable to reset password. Please sign in again." };
  }

  if (!current) {
    return { success: false, message: "Please enter your current password." };
  }

  if (!isPasswordValid(newPassword)) {
    return { success: false, message: PASSWORD_CRITERIA_INVALID_MESSAGE };
  }

  if (newPassword !== confirm) {
    return { success: false, message: "Passwords do not match." };
  }

  if (current === newPassword) {
    return { success: false, message: "New password must be different from your current password." };
  }

  const response = await callResetPasswordApi({
    email,
    role: normalizeRole(role, PROFILE_ROLE_FAMILY_MEMBER),
    currentPassword: current,
    newPassword,
  });

  if (!response) {
    return { success: false, message: "Unable to reset password. Please try again." };
  }

  return mapResetPasswordResponse(response, {
    currentField: "currentPassword",
    incorrectMessage: INCORRECT_CURRENT_PASSWORD_MESSAGE,
    successMessage: RESET_PASSWORD_SUCCESS_MESSAGE,
    failureMessage: "Unable to reset password. Please try again.",
  });
};

export const resetAuthenticatedLoginCode = async ({ emailId, currentLoginCode, loginCode, confirmLoginCode, role = PROFILE_ROLE_CAREGIVER }) => {
  const email = String(emailId || "")
    .trim()
    .toLowerCase();
  const current = sanitizeLoginCode(currentLoginCode);
  const newPassword = sanitizeLoginCode(loginCode);
  const confirm = sanitizeLoginCode(confirmLoginCode);

  if (!email) {
    return { success: false, message: "Unable to reset login code. Please sign in again." };
  }

  if (!isValidLoginCode(current)) {
    return {
      success: false,
      field: "currentLoginCode",
      message: "Please enter your current 4-digit login code.",
    };
  }

  if (!isValidLoginCode(newPassword)) {
    return { success: false, message: LOGIN_CODE_INVALID_MESSAGE };
  }

  if (newPassword !== confirm) {
    return { success: false, message: LOGIN_CODES_MISMATCH_MESSAGE };
  }

  if (current === newPassword) {
    return {
      success: false,
      message: "New login code must be different from your current login code.",
    };
  }

  const response = await callResetPasswordApi({
    email,
    role: normalizeRole(role, PROFILE_ROLE_CAREGIVER),
    currentPassword: current,
    newPassword,
  });

  if (!response) {
    return { success: false, message: "Unable to reset login code. Please try again." };
  }

  return mapResetPasswordResponse(response, {
    currentField: "currentLoginCode",
    incorrectMessage: INCORRECT_CURRENT_LOGIN_CODE_MESSAGE,
    successMessage: RESET_LOGIN_CODE_SUCCESS_MESSAGE,
    failureMessage: "Unable to reset login code. Please try again.",
  });
};
